import React, { useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import DataTableCard from './DataTableCard';

/**
 * A DataTableCard with a "create" button and a per-row edit button, each opening a dialog.
 *
 * renderCreateForm(onDone) / renderEditForm(row, onDone) return the dialog bodies.
 * extraRowActions(row) can add more buttons beside the edit button.
 */
const ManagedTableCard = ({
    title,
    exportName,
    columns,
    rows,
    emptyMessage,
    createLabel = 'Create new',
    createIcon = 'pi pi-plus',
    createHeader,
    editHeader,
    renderCreateForm,
    renderEditForm,
    extraRowActions,
    successMessage = 'Saved successfully',
}) => {
    const toast = useRef(null);
    const [activeRow, setActiveRow] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);

    const closeDialog = () => {
        setActiveRow(null);
        setDialogVisible(false);
    };

    const handleDone = async () => {
        setDialogVisible(false);
        await toast.current.show({severity: 'success', summary: 'Success', detail: successMessage});
    };

    const renderActions = (rowData) => (
        <div className="actions-container flex items-center justify-center gap-2">
            <div className="tooltip" data-tip="Edit">
                <button
                    className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                    onClick={() => {
                        setActiveRow(rowData);
                        setDialogVisible(true);
                    }}
                >
                    <i className="pi pi-pencil text-lg"></i>
                </button>
            </div>
            {extraRowActions?.(rowData)}
        </div>
    );

    const tableColumns = [...columns, {field: 'actions', header: 'Actions', body: renderActions, style: {textAlign: 'center'}}];

    const createButton = (
        <button
            className="btn btn-primary rounded-full h-10 min-h-0 shadow-sm px-6"
            onClick={() => {
                setActiveRow(null);
                setDialogVisible(true);
            }}
        >
            <i className={createIcon}></i> {createLabel}
        </button>
    );

    return (
        <DataTableCard
            title={title}
            exportName={exportName}
            columns={tableColumns}
            rows={rows}
            emptyMessage={emptyMessage}
            toolbarExtras={createButton}
            exportDisabled={false}
        >
            <Toast ref={toast}/>
            <Dialog
                header={activeRow === null ? createHeader : editHeader}
                visible={dialogVisible}
                maximizable
                style={{width: '30vw'}}
                onHide={closeDialog}
            >
                {activeRow === null ? renderCreateForm(handleDone) : renderEditForm(activeRow, handleDone)}
            </Dialog>
        </DataTableCard>
    );
};

export default ManagedTableCard;
